import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  LayoutTemplate,
  MessageSquare,
  Target,
  CalendarCheck,
  Headphones,
  BarChart3,
  Smartphone,
  TrendingUp,
  HeartHandshake,
  ArrowRight,
} from "lucide-react";
import Icon3D from "@/components/common/Icon3D";
import SplitHeading from "@/components/common/SplitHeading";

const services = [
  {
    icon: LayoutTemplate,
    title: "High-Converting Business Website",
    problem: "Your website looks dated and visitors leave without enquiring.",
    solution: "A premium, fast-loading website built to turn visitors into leads.",
    benefits: ["Modern design", "SEO-ready", "Lead-optimized"],
    result: "More qualified enquiries from the same traffic.",
  },
  {
    icon: MessageSquare,
    title: "AI WhatsApp Assistant",
    problem: "You miss leads because no one replies instantly.",
    solution: "A 24/7 AI assistant that answers, qualifies and books on WhatsApp.",
    benefits: ["Instant replies", "Auto-qualify", "Human handoff"],
    result: "Up to 70% more conversations converted to meetings.",
  },
  {
    icon: Target,
    title: "Smart Lead Capture",
    problem: "Most website visitors leave without sharing their contact details.",
    solution: "AI-powered forms, chat and popups that capture intent at the right moment.",
    benefits: ["Higher conversion", "Smart targeting", "CRM sync"],
    result: "2-3× more leads from the same ad spend.",
  },
  {
    icon: CalendarCheck,
    title: "Appointment Booking System",
    problem: "Endless back-and-forth calls and messages to fix one appointment.",
    solution: "AI books, reminds and reschedules appointments automatically.",
    benefits: ["Calendar sync", "Auto reminders", "Fewer no-shows"],
    result: "Your calendar fills itself while you focus on work.",
  },
  {
    icon: Headphones,
    title: "AI Customer Support",
    problem: "Customers wait hours for simple answers and support tickets pile up.",
    solution: "AI trained on your business answers FAQs and escalates when needed.",
    benefits: ["24/7 replies", "Instant answers", "Lower workload"],
    result: "Faster resolution and happier customers.",
  },
  {
    icon: BarChart3,
    title: "Business Analytics Dashboard",
    problem: "You don't know which marketing or sales activity is actually working.",
    solution: "One clean dashboard showing leads, bookings, support and revenue trends.",
    benefits: ["Live KPIs", "Clear reports", "Mobile-friendly"],
    result: "Make decisions based on real numbers, not guesses.",
  },
  {
    icon: Smartphone,
    title: "Mobile Responsive Design",
    problem: "Your site breaks or looks cluttered on phones.",
    solution: "Pixel-perfect responsive design that works beautifully on every device.",
    benefits: ["All devices", "Fast loading", "Better UX"],
    result: "More mobile visitors turn into paying customers.",
  },
  {
    icon: TrendingUp,
    title: "Google Analytics Integration",
    problem: "You have no visibility into where your traffic comes from.",
    solution: "Google Analytics set up to track visitors, sources and conversions.",
    benefits: ["Traffic insights", "Conversion tracking", "Ad optimisation"],
    result: "Spend smarter and grow with data-backed marketing.",
  },
  {
    icon: HeartHandshake,
    title: "30 Days Free Support",
    problem: "Most agencies disappear after launch, leaving you stuck.",
    solution: "One month of dedicated post-launch support and tweaks.",
    benefits: ["Quick fixes", "Training included", "Peace of mind"],
    result: "Your AI system keeps performing from day one.",
  },
];

export default function Services() {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const trigger = sectionRef.current;
    if (!trigger) return;
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) return;

    gsap.fromTo(
      headerRef.current,
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: {
          trigger,
          start: "top 80%",
          toggleActions: "play none none none",
        },
      }
    );

    gsap.fromTo(
      cardsRef.current.filter(Boolean),
      { opacity: 0, y: 40 },
      {
        opacity: 1,
        y: 0,
        duration: 0.7,
        stagger: 0.08,
        ease: "power3.out",
        scrollTrigger: {
          trigger,
          start: "top 75%",
          toggleActions: "play none none none",
        },
      }
    );

    return () => {
      ScrollTrigger.getAll().forEach((st) => {
        if (st.trigger === trigger) st.kill();
      });
    };
  }, []);

  return (
    <section id="services" ref={sectionRef} className="section relative" data-testid="services-section">
      <div className="container-x relative">
        <div ref={headerRef} className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-6 mb-16">
          <div>
            <span className="eyebrow"><span className="dot" /> Services</span>
            <SplitHeading
              as="h2"
              className="h-display mt-5 text-4xl sm:text-5xl lg:text-6xl text-[var(--body-fg)]"
              type="words"
              y={50}
              stagger={0.04}
              start="top 85%"
            >
              Everything included in <span className="font-serif italic gradient-text">Launch AI</span>.
            </SplitHeading>
          </div>
          <p className="max-w-md text-[var(--body-fg)]/60 text-base lg:text-lg">
            Nine essentials to launch your AI-powered business presence and start converting visitors into customers.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <article
                key={s.title}
                ref={(el) => (cardsRef.current[i] = el)}
                className="glow-border tilt-card glass rounded-3xl p-6 flex flex-col group shimmer-sweep"
                data-testid={`service-card-${i}`}
                style={{ opacity: 0 }}
              >
                <div className="flex items-center gap-3">
                  <Icon3D icon={Icon} size="h-12 w-12" iconSize="w-5 h-5" />
                  <h3 className="text-[var(--body-fg)] font-semibold text-lg tracking-tight leading-tight">{s.title}</h3>
                </div>

                <div className="mt-5 space-y-3 text-sm">
                  <Row label="Problem" value={s.problem} />
                  <Row label="Solution" value={s.solution} />
                </div>

                <ul className="mt-4 flex flex-wrap gap-1.5">
                  {s.benefits.map((b) => (
                    <li key={b} className="text-xs px-2.5 py-1 rounded-full bg-[var(--eyebrow-bg)] border border-[var(--glass-border)] text-[var(--body-fg)]/70">
                      {b}
                    </li>
                  ))}
                </ul>

                <div className="mt-5 pt-4 border-t border-[var(--hairline)] text-sm text-[var(--body-fg)]/70">
                  <span className="text-[var(--brand)] font-mono uppercase tracking-widest text-[10px] mr-2">Result</span>
                  {s.result}
                </div>

                <a
                  href="#booking"
                  className="mt-5 inline-flex items-center gap-1.5 text-sm text-[var(--brand)] hover:opacity-80 transition-opacity"
                >
                  Discuss this for my business <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </a>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[var(--body-fg)]/45 font-mono uppercase tracking-widest text-[10px]">{label}</div>
      <div className="text-[var(--body-fg)]/80 leading-snug">{value}</div>
    </div>
  );
}
